import type { ErrorEvent, EventHint } from '@sentry/nextjs';

import { attachAxiosContext } from '../contexts';
import {
  createErrorFilter,
  type ErrorPattern,
  getEventErrorMessage,
  isBrowserPluginErrorByStack,
} from '../filters';
import {
  applyApiErrorFingerprint,
  applyChunkLoadFingerprint,
  applySignalRFingerprint,
  asApiErrorLike,
  extractHttpStatus,
  NOISE_HTTP_STATUS_CODES,
} from '../fingerprint';

export type AuthState = 'authenticated' | 'guest' | 'unknown';

export interface CreateBeforeSendOptions {
  /**
   * Sentry로 보내지 않을 에러 패턴들 (string | RegExp | (error) => boolean)
   */
  ignorePatterns: readonly ErrorPattern[];
  /**
   * `ignorePatterns` 판정보다 **먼저** 평가되는 allowlist.
   * 여기에 매칭되는 에러는 ignorePatterns 에 걸려도 드롭하지 않고 통과시킨다.
   *
   * 패턴 매칭은 substring(`includes`) 방식이라 상위 패턴이 하위 패턴을 삼킬 수 있다.
   * 예: NETWORK 의 'Failed to fetch' 는 CHUNK 의
   * 'Failed to fetch dynamically imported module' 메시지에도 매칭된다.
   * 이런 경우 ignorePatterns 에서 서브셋을 "빼는" 방식(opt-out)으로는 관측이 불가능하고,
   * preservePatterns 로 명시적 우선순위를 줘야 한다. (ZEP-591 quiz 카나리 관측)
   */
  preservePatterns?: readonly ErrorPattern[];
  /**
   * `ApiErrorLike`(httpStatus/status 필드 보유) 에러 중
   * 401/403/404/422 같은 예상 가능한 HTTP 노이즈 스킵 여부. 기본 true
   */
  filterApiNoise?: boolean;
  /**
   * 브라우저 플러그인 스택 에러 스킵 여부. 기본 true
   */
  filterBrowserPlugin?: boolean;
  /**
   * 에러 발생 시점의 로그인 상태를 동기적으로 반환.
   * 지정 시 401/403 을 조건부로 처리한다:
   * - `authenticated` → Sentry 로 업로드 (권한 로직 버그 가능성 높음)
   * - `guest` / `unknown` → 기존대로 드롭 (토큰 만료 등 자연스러운 노이즈)
   *
   * 미지정 시 401/403/404/422 는 모두 드롭 (backward compat).
   * 구현은 반드시 동기 + 예외 안전해야 한다 (throw 시 `unknown` 취급).
   */
  authStateResolver?: () => AuthState;
  /**
   * `Non-Error promise rejection captured with value: undefined` 처럼
   * reason 도 stack 도 없는 unhandledrejection 슈퍼그룹의 샘플링 비율.
   * 0~1 사이. 기본 0.05 (5% 통과, 95% drop).
   *
   * QUIZ-APP-21A 처럼 한 그룹에 수만 건 쌓이는 노이즈를 줄이되,
   * 트렌드/스파이크 감시 목적으로 일부는 남긴다. 0 이면 100% drop.
   */
  blindUnhandledRejectionSampleRate?: number;
}

const AUTH_NOISE_STATUS_CODES = new Set([401, 403]);

const DEFAULT_BLIND_UR_SAMPLE_RATE = 0.05;

/**
 * Sentry 가 reason 으로 만드는 메시지 포맷은
 * `'Non-Error promise rejection captured with value: ' + String(reason)`.
 * undefined / null 두 케이스만 "진단 불가능한 슈퍼그룹"으로 본다.
 *
 * 'quota exceeded' 같은 string 거부, '[object Object]' 같은 객체 거부는
 * value 에 정보가 살아있어 fingerprint 가능하므로 blind 로 분류하지 않는다.
 */
const BLIND_UR_MESSAGES = new Set([
  'Non-Error promise rejection captured with value: undefined',
  'Non-Error promise rejection captured with value: null',
]);

/**
 * reason 도 stack 도 없는 unhandledrejection 인지 판정.
 * 진단 불가능한 슈퍼그룹(QUIZ-APP-21A 등)을 식별한다.
 */
const isBlindUnhandledRejection = (event: ErrorEvent): boolean => {
  const values = event.exception?.values;
  if (!values || values.length !== 1) return false;
  const ex = values[0];
  if (ex?.mechanism?.type !== 'onunhandledrejection') return false;
  if (!ex.value || !BLIND_UR_MESSAGES.has(ex.value)) return false;
  const frames = ex.stacktrace?.frames;
  return !frames || frames.length === 0;
};

const safeResolveAuthState = (
  resolver: (() => AuthState) | undefined,
): AuthState => {
  if (!resolver) return 'unknown';
  try {
    return resolver();
  } catch {
    return 'unknown';
  }
};

/**
 * `Sentry.init({ beforeSend })`에 넘길 공통 beforeSend 함수를 만든다.
 *
 * 수행 순서:
 * 1. 브라우저 플러그인 스택 에러 스킵
 * 2. `preservePatterns` 매칭 시 ignore 판정을 건너뛰고 통과 (allowlist 우선)
 * 3. `ignorePatterns`에 걸리는 에러 스킵
 * 4. `ApiErrorLike`이면 HTTP 노이즈 드롭 여부 결정 + fingerprint 적용
 *    - 401/403: `authStateResolver` 가 `authenticated` 를 반환하면 업로드
 *    - 그 외 NOISE_HTTP_STATUS_CODES(404/422 등)는 드롭
 */
export const createBeforeSend = ({
  ignorePatterns,
  preservePatterns,
  filterApiNoise = true,
  filterBrowserPlugin = true,
  authStateResolver,
  blindUnhandledRejectionSampleRate = DEFAULT_BLIND_UR_SAMPLE_RATE,
}: CreateBeforeSendOptions) => {
  const shouldIgnoreError = createErrorFilter({ patterns: ignorePatterns });
  const shouldPreserveError =
    preservePatterns && preservePatterns.length > 0
      ? createErrorFilter({ patterns: preservePatterns })
      : null;

  return (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    if (filterBrowserPlugin && isBrowserPluginErrorByStack(event)) {
      return null;
    }

    // reason/stack 없는 unhandledrejection 슈퍼그룹은 샘플링 드롭.
    // 통과한 이벤트는 태그로 표시해 대시보드에서 가중치 계산 가능.
    if (isBlindUnhandledRejection(event)) {
      if (Math.random() >= blindUnhandledRejectionSampleRate) {
        return null;
      }
      if (!event.tags) event.tags = {};
      event.tags['blind_unhandled_rejection'] = 'true';
      event.tags['blind_ur_sample_rate'] = String(
        blindUnhandledRejectionSampleRate,
      );
    }

    const error = hint.originalException;
    // preservePatterns 매칭 시 ignore 판정을 건너뛴다 (allowlist 가 ignore 보다 우선).
    // substring 매칭 특성상 상위 ignore 패턴('Failed to fetch' 등)이 보존 대상 메시지를
    // 삼키는 것을 막는 유일한 방법이다.
    const isPreserved =
      shouldPreserveError !== null &&
      (shouldPreserveError(error) ||
        shouldPreserveError(getEventErrorMessage(event)));
    // hint.originalException 으로 매칭하고, null(주입/onerror 에러)인 경우엔
    // 이벤트 메시지로 fallback 매칭한다. (getEventErrorMessage 참고)
    if (
      !isPreserved &&
      (shouldIgnoreError(error) ||
        shouldIgnoreError(getEventErrorMessage(event)))
    ) {
      console.error('Sentry Report Prevented Error =>', error ?? event.message);
      return null;
    }

    const apiError = asApiErrorLike(error);
    // fallback: annotateAxiosError가 안 씌워진 native AxiosError / response.status만
    // 보유한 에러도 같은 노이즈 정책을 받아야 한다 (PLAY-APP-A8W 14일 2095건 누수 차단).
    const httpStatus = apiError?.httpStatus ?? extractHttpStatus(error);

    if (httpStatus !== null) {
      const isNoise = NOISE_HTTP_STATUS_CODES.has(httpStatus);
      const isAuthError = AUTH_NOISE_STATUS_CODES.has(httpStatus);
      const authState = safeResolveAuthState(authStateResolver);
      const rescueAuthError = isAuthError && authState === 'authenticated';

      if (filterApiNoise && isNoise && !rescueAuthError) {
        if (httpStatus === 401) {
          console.warn('[Sentry filtered] 401 Unauthorized =>', error);
        }
        return null;
      }

      if (apiError !== null) {
        applyApiErrorFingerprint(event, apiError);
      } else {
        // native AxiosError 등 ApiErrorLike이 아닌 케이스에도 대시보드에서 필터할 수
        // 있게 status 태그만 최소한으로 남긴다. fingerprint는 Sentry 기본값 유지.
        if (!event.tags) event.tags = {};
        event.tags['api_http_status'] = String(httpStatus);
      }
      if (authStateResolver) {
        if (!event.tags) event.tags = {};
        event.tags['auth_state'] = authState;
      }
    }

    // SignalR 연결 실패를 단일 그룹으로 수렴시킨다.
    // chunk hash가 빌드마다 달라도 같은 이슈로 묶인다.
    applySignalRFingerprint(event);

    // 청크 로드 실패를 단일 그룹('chunk-load-error')으로 수렴시킨다 (ZEP-591 카나리 관측).
    // ignore 필터 통과 이후 단계이므로, CHUNK_LOAD_ERROR_PATTERNS 를 ignorePatterns 로
    // 쓰는 앱들(quiz-app 외 전부)은 애초에 드롭되어 영향이 없다.
    applyChunkLoadFingerprint(event);

    // axios/ZepQuizApiError 페이로드를 contexts.axios에 부착해 디버깅 가능하게 한다.
    // 위 노이즈 필터 통과 후에만 호출되므로 드랍될 이벤트에 대한 직렬화 비용은 없다.
    attachAxiosContext(event, error);

    return event;
  };
};
